/**
 * Préparer mes visites hors ligne — à faire AVANT de partir en zone sans réseau.
 *
 * Part de MA liste « Visites à réaliser » (en retard + à venir), coche par défaut
 * celles des N prochains jours, et télécharge dans l'appareil tout ce qu'il faut pour
 * les réaliser sans réseau (voir services/offlineService.js#preparerVisites).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { apiGet } from '../../utils/api';
import { localeCourante } from '../../i18n';
import { preparerVisites } from '../../services/offlineService';
import { cleVisite, estPreparable, selectionParDefaut } from '../../utils/visitesHorsLigne';

const HORIZONS = [7, 14, 30];

const aujourdhuiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PreparationHorsLigneModal = ({ tenantSlug, user, onClose, onTermine }) => {
  const { t } = useTranslation();
  const [visites, setVisites] = useState(null);
  // Mes bâtiments PAS ENCORE DUS (hors « Visites à réaliser ») : proposés pour avancer
  // la prévention hors ligne, jamais cochés par défaut.
  const [batimentsAJour, setBatimentsAJour] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [horizon, setHorizon] = useState(7);
  const [selection, setSelection] = useState(new Set());
  const [progres, setProgres] = useState(null); // { fait, total } pendant le téléchargement
  const [resultat, setResultat] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet(tenantSlug, '/prevention/visites-a-realiser?inclure_batiments_a_jour=true');
        const liste = res?.visites || [];
        setBatimentsAJour(res?.batiments_a_jour || []);
        setVisites(liste);
        setSelection(selectionParDefaut(liste, 7, aujourdhuiIso()));
      } catch (e) {
        setErreur(e?.message || String(e));
      }
    })();
  }, [tenantSlug]);

  const changerHorizon = (jours) => {
    setHorizon(jours);
    setSelection(selectionParDefaut(visites, jours, aujourdhuiIso()));
  };

  const basculer = (cle) => setSelection((prev) => {
    const s = new Set(prev);
    if (s.has(cle)) s.delete(cle); else s.add(cle);
    return s;
  });

  const preparables = useMemo(() => (visites || []).filter(estPreparable), [visites]);
  const choisies = [...preparables, ...batimentsAJour.filter(estPreparable)]
    .filter((v) => selection.has(cleVisite(v)));

  const telecharger = async () => {
    setProgres({ fait: 0, total: choisies.length });
    try {
      // Le code des écrans de visite est chargé MAINTENANT : sans réseau, l'application
      // ne pourrait plus le télécharger au moment d'ouvrir une visite.
      await Promise.all([
        import('../Prevention'),
        import('../VisitesARealiser'),
        import('../InspectionComponents'),
      ]);
      const res = await preparerVisites({
        tenantSlug,
        userId: user.id,
        visites: choisies,
        apiGet,
        onProgres: (fait, total) => setProgres({ fait, total }),
      });
      setResultat(res);
      if (onTermine) onTermine(res);
    } catch (e) {
      setResultat({ preparees: 0, echecs: [], erreur: e?.message || String(e) });
    } finally {
      setProgres(null);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(`${d}T00:00:00`).toLocaleDateString(localeCourante()); } catch { return d; }
  };

  const ligneVisite = (v) => {
      const cle = cleVisite(v);
      const ok = estPreparable(v);
      return (
        <label key={cle} style={{
          display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#f8f9fa', padding: '10px 12px',
          borderRadius: '6px', marginBottom: '8px', border: '1px solid #dee2e6',
          cursor: ok ? 'pointer' : 'not-allowed', opacity: ok ? 1 : 0.6,
        }}>
          <input type="checkbox" checked={selection.has(cle)} disabled={!ok} onChange={() => basculer(cle)} style={{ marginTop: '4px' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold' }}>🏢 {v.nom || '—'}</div>
            {v.adresse && <div style={{ fontSize: '12px', color: '#6c757d' }}>📍 {v.adresse}</div>}
            <div style={{ fontSize: '12px', color: v.statut === 'en_retard' ? '#b91c1c' : '#6c757d' }}>
              📅 {fmtDate(v.date)}
              {v.statut === 'en_retard' ? ` · ${t('horsLigne.enRetard')}` : ''}
              {v.source === 'rendez_vous' ? ` · ${t('horsLigne.rendezVous')}` : ` · ${t('horsLigne.aInspecter')}`}
              {!ok ? ` · ${t('horsLigne.sansBatiment')}` : ''}
            </div>
          </div>
        </label>
      );
  };

  return (
    <div className="modal-overlay" onClick={progres ? undefined : onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2>📥 {t('horsLigne.preparerTitre')}</h2>
          <button className="close-btn" onClick={onClose} disabled={!!progres}>✕</button>
        </div>

        <div className="modal-body">
          {resultat ? (
            <div>
              <div style={{
                background: resultat.erreur ? '#f8d7da' : '#d1f2eb', padding: '15px', borderRadius: '8px', marginBottom: '15px',
                border: `2px solid ${resultat.erreur ? '#dc3545' : '#28a745'}`,
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
                  {resultat.erreur
                    ? `❌ ${t('horsLigne.preparationEchouee')}`
                    : `✅ ${t('horsLigne.visitesPreparees', { count: resultat.preparees })}`}
                </div>
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  {resultat.erreur || t('horsLigne.ouRetrouver')}
                </div>
              </div>
              {resultat.echecs?.length > 0 && (
                <div style={{ background: '#fff3cd', padding: '12px', borderRadius: '6px', fontSize: '13px', marginBottom: '15px' }}>
                  ⚠️ {t('horsLigne.echecsPreparation', { count: resultat.echecs.length })}
                  <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                    {resultat.echecs.map((e, i) => <li key={i}>{e.nom} — {e.erreur}</li>)}
                  </ul>
                </div>
              )}
              <Button onClick={onClose} style={{ width: '100%' }}>{t('horsLigne.fermer')}</Button>
            </div>
          ) : erreur ? (
            <div style={{ background: '#f8d7da', padding: '15px', borderRadius: '8px', color: '#721c24' }}>
              ❌ {t('horsLigne.listeIndisponible')} ({erreur})
            </div>
          ) : !visites ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>⏳ {t('horsLigne.chargement')}</div>
          ) : (
            <>
              <div style={{ background: '#e7f3ff', padding: '12px 15px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px' }}>
                {t('horsLigne.preparerExplication')}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '14px' }}>
                <span>{t('horsLigne.cocherJusqua')}</span>
                {HORIZONS.map((j) => (
                  <Button key={j} size="sm" variant={horizon === j ? 'default' : 'outline'} onClick={() => changerHorizon(j)}>
                    {t('horsLigne.jours', { count: j })}
                  </Button>
                ))}
                <Button size="sm" variant="outline" onClick={() => setSelection(new Set(preparables.map(cleVisite)))}>
                  {t('horsLigne.toutCocher')}
                </Button>
              </div>

              <div style={{ maxHeight: '380px', overflowY: 'auto', marginBottom: '15px' }}>
                {visites.length === 0 && (
                  <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6c757d' }}>{t('horsLigne.aucuneVisiteARealiser')}</div>
                )}
                {visites.map(ligneVisite)}
                {batimentsAJour.length > 0 && (
                  <div style={{ fontWeight: 'bold', fontSize: '14px', margin: '14px 0 8px', color: '#374151' }}>
                    {t('horsLigne.mesAutresBatiments', { count: batimentsAJour.length })}
                  </div>
                )}
                {batimentsAJour.map(ligneVisite)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <Button variant="outline" onClick={onClose} disabled={!!progres}>{t('horsLigne.annuler')}</Button>
                <Button onClick={telecharger} disabled={!!progres || choisies.length === 0} style={{ background: '#28a745' }}>
                  {progres
                    ? `⏳ ${t('horsLigne.telechargement', { fait: progres.fait, total: progres.total })}`
                    : `✅ ${t('horsLigne.telecharger', { count: choisies.length })}`}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreparationHorsLigneModal;
